import { useState, useEffect, useCallback, useMemo } from 'react';
import type { TFunction } from 'i18next';
import { useProfileContext } from './useProfileContext';
import { useMusicians } from './useMusicians';
import { useCrew } from './useCrew';
import { useSuggestionList } from './useSuggestionList';
import { apiGetTeam, apiUpdateTeamMember } from '../api';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';
import { buildPersonInstruments } from '../lib/instrumentConflict';
import { buildPersonTags } from '../lib/rackUsage';
import { isNameUnique } from '../utils/slug';
import type { DietType, Instrument, Musician, CrewMember } from '../types';

// The rider cards (musician / crew) share ONE shape of editor behaviour:
//   - the diet fields no longer live on the card — they live on the Team
//     identity (`team_members`) and this hook mirrors + writes them through;
//   - the cross-referenced "other people" views (instruments / tags) used by
//     the rack-usage conflict panel;
//   - the explicit Save guard (name required + name uniqueness) with its
//     saving flag and its save-error reporting.
// This hook encapsulates EXACTLY that. It deliberately does NOT hold the card
// state itself (each page owns its own entity type + its own new-entity
// default), nor the tag/instrument/backline editing handlers (those diverge
// between the two pages and stay on the pages).

export type TeamDietEntry = { diet?: string; dietNotes?: string; nonFoodAllergies?: string };

// A relaxed view over the two card types for the pieces this hook needs.
// `tags` and `instruments` are the only fields the shared views touch.
export type EditorEntity = {
  id: string;
  name: string;
  diet?: DietType | string;
  dietNotes?: string;
  nonFoodAllergies?: string;
  tags?: string[];
  instruments?: Instrument[];
};

// The pages' own card wrapper type.
export type CardSetter<T> = (updater: T | null | ((prev: T | null) => T | null)) => void;

export type EntityEditorInput<T extends EditorEntity> = {
  /**
   * Save-error scope reported when `onSave` throws. Deliberately page-supplied
   * and NOT derived: the Musician page reported 'musicians' while the Crew page
   * reported 'team' before this extraction, so keeping each page's own scope
   * preserves the banner text the user actually saw.
   */
  scope: 'musicians' | 'crew' | 'team';
  /** The current card (null before it is loaded). */
  entity: T | null;
  /**
   * The card update function, already wrapped so it propagates to the list.
   * Generic over the page's own card type so the narrower `Musician` /
   * `CrewMember` setter is accepted — this hook only ever patches the diet
   * fields, which exist on both card types.
   */
  setEntity: CardSetter<T>;
  /** The guard: returns the alert text when the name is not acceptable, else null. */
  validate: (entity: EditorEntity, t: TFunction) => string | null;
  /** The write performed after validation succeeded. */
  onSave: () => Promise<void>;
  /** Translation of the page (for the validation/save alerts). */
  t: TFunction;
  /** Alert text when `onSave` throws (page-owned i18n key). */
  failedSave: string;
};

export type EntityEditorResult = {
  saving: boolean;
  /** Resolved diet value for display: Team wins, legacy card as fallback. */
  dietValue: string;
  dietNotesValue: string;
  nonFoodAllergiesValue: string;
  /** Suggested diet types (shared 'diet-types' suggestion list). */
  dietTypes: string[];
  /** Write a diet patch through to Team, falling back to the card fields. */
  updateDiet: (patch: { diet?: DietType | ''; dietNotes?: string; nonFoodAllergies?: string }) => void;
  /** Cross-referenced person→instruments map (rack-usage conflict panel). */
  personInstruments: ReturnType<typeof buildPersonInstruments>;
  /** Cross-referenced person→tags map (rack-usage conflict panel). */
  personTags: ReturnType<typeof buildPersonTags>;
  /** The Save handler: guard, then run `onSave`, reporting failures. */
  handleSave: () => Promise<void>;
};

export function useEntityEditor<T extends EditorEntity>(input: EntityEditorInput<T>): EntityEditorResult {
  const { scope, entity, setEntity, validate, onSave, t, failedSave } = input;
  const { activeProfileId } = useProfileContext();
  const { musicians } = useMusicians();
  const { crew } = useCrew();
  const { types: dietTypes } = useSuggestionList('diet-types');

  const [saving, setSaving] = useState(false);

  // Cross-referenced people views (shared by the musician AND crew editors: the
  // conflict engine needs to see everyone, not just the current card).
  const personInstruments = useMemo(
    () => buildPersonInstruments(musicians, crew),
    [musicians, crew],
  );
  const personTags = useMemo(
    () => buildPersonTags(musicians, crew),
    [musicians, crew],
  );

  // Diet lives on the Team identity (team_members) — the rider card mirrors it
  // for display and falls back to the legacy card value when the id has no Team
  // row. Editing here writes through to Team (source of truth). (2026-08-09)
  const [teamDiet, setTeamDiet] = useState<Record<string, TeamDietEntry>>({});
  useEffect(() => {
    if (!activeProfileId) { setTeamDiet({}); return; }
    let cancelled = false;
    apiGetTeam({ band: activeProfileId }).then((members) => {
      if (cancelled) return;
      const map: Record<string, TeamDietEntry> = {};
      for (const m of members) map[m.id] = { diet: m.diet || '', dietNotes: m.diet_notes || '', nonFoodAllergies: m.non_food_allergies || '' };
      setTeamDiet(map);
    }).catch(() => { if (!cancelled) setTeamDiet({}); });
    return () => { cancelled = true; };
  }, [activeProfileId]);

  // Resolved diet for display: Team wins, legacy card as fallback.
  const dietValue = entity
    ? (teamDiet[entity.id]?.diet ?? (entity.diet === 'none' ? '' : (entity.diet || '')))
    : '';
  const dietNotesValue = entity
    ? (teamDiet[entity.id]?.dietNotes ?? entity.dietNotes ?? '')
    : '';
  const nonFoodAllergiesValue = entity
    ? (teamDiet[entity.id]?.nonFoodAllergies ?? entity.nonFoodAllergies ?? '')
    : '';

  // Write diet through to team_members (source of truth). If the card id has no
  // Team row (legacy card), fall back to the card fields so nothing is lost.
  const updateDiet = useCallback((patch: { diet?: DietType | ''; dietNotes?: string; nonFoodAllergies?: string }) => {
    if (!entity) return;
    const team = teamDiet[entity.id];
    if (team) {
      const next = {
        diet: patch.diet !== undefined ? patch.diet : team.diet,
        dietNotes: patch.dietNotes !== undefined ? patch.dietNotes : team.dietNotes,
        nonFoodAllergies: patch.nonFoodAllergies !== undefined ? patch.nonFoodAllergies : team.nonFoodAllergies,
      };
      setTeamDiet(prev => ({ ...prev, [entity.id]: next }));
      apiUpdateTeamMember(entity.id, {
        diet: next.diet as string,
        diet_notes: next.dietNotes as string,
        non_food_allergies: next.nonFoodAllergies as string,
      })
        .then(() => reportSaveOk('team'))
        .catch(err => reportSaveError('team', err));
    } else {
      const dietNotes = patch.dietNotes;
      if (patch.diet !== undefined) setEntity(prev => prev ? { ...prev, diet: patch.diet as DietType } : prev);
      if (dietNotes !== undefined) setEntity(prev => prev ? { ...prev, dietNotes } : prev);
      if (patch.nonFoodAllergies !== undefined) setEntity(prev => prev ? { ...prev, nonFoodAllergies: patch.nonFoodAllergies as string } : prev);
    }
  }, [entity, teamDiet, setEntity]);

  const handleSave = useCallback(async () => {
    if (!entity) return;
    const failure = validate(entity, t);
    if (failure) {
      alert(failure);
      return;
    }
    setSaving(true);
    try {
      await onSave();
    } catch (err) {
      reportSaveError(scope, err);
      alert(failedSave);
    } finally {
      setSaving(false);
    }
  }, [entity, validate, onSave, scope, t, failedSave]);

  return {
    saving,
    dietValue,
    dietNotesValue,
    nonFoodAllergiesValue,
    dietTypes,
    updateDiet,
    personInstruments,
    personTags,
    handleSave,
  };
}

// Shared uniqueness rule — the two pages keep their own message keys, but the
// check itself is implemented once here.
export function entityNameUnique(
  name: string,
  list: Array<{ id: string; name?: string; role?: string }>,
  id: string,
): boolean {
  return isNameUnique(name, list as never, id);
}

export type { Musician, CrewMember };
